import { z } from "zod";

/**
 * Artikelregeln je Kunde: WENN Bedingungen (alle müssen zutreffen) DANN Felder setzen – z. B. „Name enthält T-Shirt →
 * Gewicht 0,2 kg, HS-Code 61091000“. Versioniert gespeichert (product_rule_sets), im Portal gepflegt.
 *
 * Auswertung: Regeln in Reihenfolge, je Feld gewinnt die erste zutreffende Regel. „Nur wenn leer“ setzt nur fehlende
 * Werte, „immer“ überschreibt. Von Hand im Portal gepflegte (geschützte) Felder lässt die Anwendung immer aus.
 * Neue Bedingungen/Aktionen: hier ergänzen, kein DB-Umbau.
 */

const text = z.string().trim().min(1).max(200);
const condition = z.discriminatedUnion("field", [
  /** Freitext-Felder: enthält eines der Wörter / beginnt mit / ist genau (Groß-/Kleinschreibung egal) */
  z.object({ field: z.enum(["name", "sku", "manufacturer", "productType"]), op: z.enum(["contains", "not_contains", "starts_with", "equals"]), value: z.array(text).min(1).max(50) }),
  /** Shopify-Tags */
  z.object({ field: z.literal("tag"), op: z.enum(["has", "has_not"]), value: z.array(text).min(1).max(50) }),
  /** Feld leer / gefüllt */
  z.object({ field: z.literal("empty"), op: z.enum(["is_empty", "is_set"]), value: z.enum(["weight", "dimensions", "hsCode", "countryOfOrigin", "gtin", "customsDescription", "customsValue"]) }),
]);
export type ProductCondition = z.infer<typeof condition>;

const mode = z.enum(["if_empty", "always"]).default("if_empty");
const action = z.discriminatedUnion("type", [
  z.object({ type: z.literal("setWeight"), kg: z.number().positive().max(1000), mode }),
  z.object({ type: z.literal("setDimensions"), lengthCm: z.number().positive().max(1000), widthCm: z.number().positive().max(1000), heightCm: z.number().positive().max(1000), mode }),
  z.object({ type: z.literal("setHsCode"), code: z.string().trim().regex(/^\d{6,10}$/, "HS-Code: 6–10 Ziffern"), mode }),
  z.object({ type: z.literal("setCountryOfOrigin"), country: z.string().trim().length(2).transform((s) => s.toUpperCase()), mode }),
  z.object({ type: z.literal("setCustomsDescription"), text: z.string().trim().min(1).max(200), mode }),
  z.object({ type: z.literal("setCustomsValue"), eur: z.number().min(0).max(1_000_000), mode }),
  z.object({ type: z.literal("setFragile"), fragile: z.boolean(), mode: z.literal("always").default("always") }),
]);
export type ProductAction = z.infer<typeof action>;

export const productRuleSchema = z.object({
  id: z.string().min(1).max(64),
  name: z.string().trim().min(1, "Name der Regel fehlt").max(80),
  enabled: z.boolean().default(true),
  /** alle Bedingungen müssen zutreffen; leer = gilt für alle Artikel */
  when: z.array(condition).max(20).default([]),
  then: z.array(action).min(1, "Mindestens eine Aktion").max(20),
});
export type ProductRuleDef = z.infer<typeof productRuleSchema>;

export const productRuleSetSchema = z.object({ rules: z.array(productRuleSchema).max(200).default([]) });
export type ProductRuleSet = z.infer<typeof productRuleSetSchema>;
export const EMPTY_PRODUCT_RULE_SET: ProductRuleSet = { rules: [] };

/** Werte eines Artikels, auf die Regeln schauen bzw. die sie setzen (Spaltennamen wie in der Datenbank) */
export interface ProductRuleFacts {
  name: string;
  sku: string;
  manufacturer: string | null;
  productType: string | null;
  tags: string[];
  grossWeightKg: number | null;
  lengthCm: number | null;
  widthCm: number | null;
  heightCm: number | null;
  hsCode: string | null;
  countryOfOrigin: string | null;
  gtin: string | null;
  customsDescription: string | null;
  customsValueMinor: number | null;
  fragile: boolean;
}

/** Spaltenänderungen, die eine Regelanwendung ergibt */
export interface ProductRulePatch {
  grossWeightKg?: number;
  lengthCm?: number;
  widthCm?: number;
  heightCm?: number;
  hsCode?: string;
  countryOfOrigin?: string;
  customsDescription?: string;
  customsValueMinor?: number;
  fragile?: boolean;
}

/** Feldpfad für den Schreibschutz (lockedFields) je gesetzter Spalte */
export const PRODUCT_RULE_FIELD_PATH: Record<keyof ProductRulePatch, string> = {
  grossWeightKg: "weight.grossKg",
  lengthCm: "dimensions",
  widthCm: "dimensions",
  heightCm: "dimensions",
  hsCode: "customs.hsCode",
  countryOfOrigin: "customs.countryOfOrigin",
  customsDescription: "customsDescription",
  customsValueMinor: "customs.customsValue",
  fragile: "fragile",
};

const lc = (s: string | null | undefined) => (s ?? "").toLowerCase();
const isEmpty = (f: ProductRuleFacts, which: Extract<ProductCondition, { field: "empty" }>["value"]) => {
  switch (which) {
    case "weight": return !(f.grossWeightKg && f.grossWeightKg > 0);
    case "dimensions": return !(f.lengthCm && f.widthCm && f.heightCm);
    case "hsCode": return !f.hsCode;
    case "countryOfOrigin": return !f.countryOfOrigin;
    case "gtin": return !f.gtin;
    case "customsDescription": return !f.customsDescription;
    case "customsValue": return f.customsValueMinor == null;
  }
};

export function productConditionMatches(c: ProductCondition, f: ProductRuleFacts): boolean {
  if (c.field === "tag") {
    const tags = f.tags.map(lc);
    const has = c.value.some((v) => tags.includes(lc(v)));
    return c.op === "has" ? has : !has;
  }
  if (c.field === "empty") return c.op === "is_empty" ? isEmpty(f, c.value) : !isEmpty(f, c.value);
  const hay = lc(c.field === "name" ? f.name : c.field === "sku" ? f.sku : c.field === "manufacturer" ? f.manufacturer : f.productType);
  const values = c.value.map(lc);
  switch (c.op) {
    case "contains": return values.some((v) => hay.includes(v));
    case "not_contains": return !values.some((v) => hay.includes(v));
    case "starts_with": return values.some((v) => hay.startsWith(v));
    case "equals": return values.some((v) => hay === v);
  }
}

export interface ProductRuleResult {
  patch: ProductRulePatch;
  /** je Regel die Spalten, die sie gesetzt hat */
  applied: { id: string; name: string; fields: (keyof ProductRulePatch)[] }[];
}

/**
 * Regeln auswerten. locked = geschützte Feldpfade (von Hand gepflegt) – dort setzt keine Regel etwas.
 * Erste zutreffende Regel je Feld gewinnt; „nur wenn leer“ prüft den Wert vor jeder Regel.
 */
export function evaluateProductRules(f: ProductRuleFacts, set: ProductRuleSet, locked: readonly string[] = []): ProductRuleResult {
  const patch: ProductRulePatch = {};
  const applied: ProductRuleResult["applied"] = [];
  // Gewicht: Schutz kann unter Brutto oder Netto stehen (beide Spalten sind ein Feld)
  const isLocked = (col: keyof ProductRulePatch) => locked.includes(PRODUCT_RULE_FIELD_PATH[col]) || (col === "grossWeightKg" && locked.includes("weight.netKg"));
  const free = (col: keyof ProductRulePatch) => !(col in patch) && !isLocked(col);
  for (const r of set.rules) {
    if (!r.enabled || !r.when.every((c) => productConditionMatches(c, f))) continue;
    const fields: (keyof ProductRulePatch)[] = [];
    const put = <K extends keyof ProductRulePatch>(col: K, v: NonNullable<ProductRulePatch[K]>, emptyNow: boolean, m: "if_empty" | "always") => {
      if (!free(col) || (m === "if_empty" && !emptyNow)) return;
      if ((f as unknown as Record<string, unknown>)[col] === v) return; // schon so
      patch[col] = v;
      fields.push(col);
    };
    for (const a of r.then) {
      switch (a.type) {
        case "setWeight": put("grossWeightKg", a.kg, isEmpty(f, "weight"), a.mode); break;
        case "setDimensions": {
          if (!free("lengthCm") || (a.mode === "if_empty" && !isEmpty(f, "dimensions"))) break;
          if (f.lengthCm === a.lengthCm && f.widthCm === a.widthCm && f.heightCm === a.heightCm) break;
          patch.lengthCm = a.lengthCm; patch.widthCm = a.widthCm; patch.heightCm = a.heightCm;
          fields.push("lengthCm", "widthCm", "heightCm");
          break;
        }
        case "setHsCode": put("hsCode", a.code, isEmpty(f, "hsCode"), a.mode); break;
        case "setCountryOfOrigin": put("countryOfOrigin", a.country, isEmpty(f, "countryOfOrigin"), a.mode); break;
        case "setCustomsDescription": put("customsDescription", a.text, isEmpty(f, "customsDescription"), a.mode); break;
        case "setCustomsValue": put("customsValueMinor", Math.round(a.eur * 100), isEmpty(f, "customsValue"), a.mode); break;
        case "setFragile": put("fragile", a.fragile, true, "always"); break;
      }
    }
    if (fields.length) applied.push({ id: r.id, name: r.name, fields });
  }
  return { patch, applied };
}

/** Feldnamen für Verlauf und Vorschau */
export const PRODUCT_RULE_FIELD_LABEL: Record<keyof ProductRulePatch, string> = {
  grossWeightKg: "Gewicht",
  lengthCm: "Länge",
  widthCm: "Breite",
  heightCm: "Höhe",
  hsCode: "HS-Code",
  countryOfOrigin: "Ursprungsland",
  customsDescription: "Zollbeschreibung",
  customsValueMinor: "Zollwert",
  fragile: "Zerbrechlich",
};

/** Gewicht stammt aus dem Standardgewicht des Kunden (nicht von Hand gepflegt, noch gleich dem übernommenen Wert) */
export function isDefaultWeight(p: { grossWeightKg: number | null; weightDefaultKg?: number | null; lockedFields?: readonly string[] }): boolean {
  if (p.weightDefaultKg == null || p.grossWeightKg == null) return false;
  if (p.lockedFields?.includes("weight.grossKg") || p.lockedFields?.includes("weight.netKg")) return false;
  return Math.abs(Number(p.grossWeightKg) - Number(p.weightDefaultKg)) < 1e-9;
}
